import axios from 'axios';
import { DEMO, demoAdapter } from './demo';

// Auth is carried by an httpOnly cookie (set by the backend on login/register).
// No token is ever stored in localStorage (XSS hardening).
// 45s default timeout so a slow/cold backend can't hang a page forever with an
// endless spinner. Long operations (file upload) override this per-call.
const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true, timeout: 45000 });

// DEMO MODE: short-circuit every request to local fixtures — no backend, no auth,
// no connection to production. Only active in the dedicated demo build.
if (DEMO) api.defaults.adapter = demoAdapter;

// A SINGLE transient 401 must NOT nuke the session. The backend re-checks the
// account on every request (with a short cache) and can briefly 401 on a cache
// miss / cookie-timing race / momentary hiccup — especially while a chatter is
// actively sending. The old code bounced the WHOLE app to /auth/login on any 401,
// which is the "ça redémarre pendant l'envoi / faut actualiser la page" the
// chatters reported. Instead: verify the session ONCE; only log out if it's truly
// dead, otherwise replay the original request transparently.
let _sessionDead = false;
let _verifyPromise = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const is401 = err.response?.status === 401;
    const isAuthCall = cfg.url?.includes('/auth/');
    if (is401 && !isAuthCall && !cfg._retry401 && !_sessionDead) {
      cfg._retry401 = true;
      try {
        // Dedupe a burst of concurrent 401s into ONE session check.
        _verifyPromise = _verifyPromise || api.get('/auth/me').finally(() => { _verifyPromise = null; });
        await _verifyPromise;        // session still valid → the 401 was transient
        return api(cfg);             // replay the original call, no disruption
      } catch {
        // /auth/me itself failed → genuinely logged out → go to login (once).
        if (!_sessionDead) { _sessionDead = true; window.location.href = '/auth/login'; }
      }
    }
    // Subscription inactive (card required to start, or past due) → send them to
    // checkout instead of throwing a wall of errors. Skip on billing/auth pages.
    if (err.response?.status === 403 && err.response?.data?.billing_required) {
      const p = window.location.pathname;
      if (!p.startsWith('/subscribe') && !p.startsWith('/auth')) {
        window.location.href = '/subscribe?plan=solo';
      }
    }
    // 25/09 : option absente du plan (402). L'ecran est normalement deja masque ;
    // ce cas arrive sur un lien direct ou un ancien onglet -> on l'envoie choisir un plan.
    if (err.response?.status === 402 && err.response?.data?.upgrade_required) {
      const p = window.location.pathname;
      if (!p.startsWith('/subscribe') && !p.startsWith('/auth') && !p.startsWith('/dashboard/subscriptions')) {
        window.location.href = '/dashboard/subscriptions?option=' + err.response.data.upgrade_required;
      }
    }
    return Promise.reject(err);
  }
);

export const apiError = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (d && typeof d === 'object') {
    if (d.code === 'floodwait') {
      const s = d.seconds || 0;
      const human = s < 60 ? `${s}s` : s < 3600 ? `${Math.round(s / 60)}min` : `${Math.floor(s / 3600)}h${Math.round((s % 3600) / 60)}min`;
      return `Telegram FloodWait — patiente ${human} avant de réessayer`;
    }
    return d.message || JSON.stringify(d);
  }
  return d || fallback;
};

/** Returns a structured FloodWait info object {seconds, account_id} or null. */
export const extractFloodwait = (err) => {
  const d = err?.response?.data?.detail;
  if (d && typeof d === 'object' && d.code === 'floodwait') {
    return { seconds: Number(d.seconds) || 0, account_id: d.account_id, message: d.message };
  }
  return null;
};

export const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '');

// All time displays use Europe/Paris (Paris time) — regardless of viewer's local timezone.
const PARIS_TZ = 'Europe/Paris';
// Backend stores naive UTC timestamps (no trailing Z) — without this, browsers
// parse them as LOCAL time and viewers outside France see shifted hours.
export const parseUTC = (iso) => {
  const s = String(iso);
  return new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + 'Z');
};
export const fmtTimeParis = (iso) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: PARIS_TZ }).format(parseUTC(iso));
  } catch { return ''; }
};
// Jour d'un message pour les SÉPARATEURS DE DATE du fil (04/09, demande Marlon) :
// « aujourd'hui » / « hier » / « mercredi 27 août ». Clé de comparaison = fmtDayKeyParis.
export const fmtDayKeyParis = (iso) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('fr-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: PARIS_TZ }).format(parseUTC(iso));
  } catch { return ''; }
};
export const fmtDayLabelParis = (iso) => {
  const k = fmtDayKeyParis(iso);
  if (!k) return '';
  const today = fmtDayKeyParis(new Date().toISOString());
  const yest = fmtDayKeyParis(new Date(Date.now() - 86400000).toISOString());
  if (k === today) return "aujourd'hui";
  if (k === yest) return 'hier';
  try {
    const opts = { weekday: 'long', day: 'numeric', month: 'long', timeZone: PARIS_TZ };
    // L'année n'apparaît que si le message n'est pas de l'année en cours.
    if (k.slice(0, 4) !== today.slice(0, 4)) opts.year = 'numeric';
    return new Intl.DateTimeFormat('fr-FR', opts).format(parseUTC(iso));
  } catch { return k; }
};

export const fmtDateTimeParis = (iso) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: PARIS_TZ,
    }).format(parseUTC(iso));
  } catch { return ''; }
};

export const fmtDateTime = (iso) => fmtDateTimeParis(iso);

export const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Math.max(0, Date.now() - parseUTC(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return `${Math.floor(d / 7)}w`;
};

export const mediaUrl = (u) => (u ? (u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : null);

export const fmtResponse = (s) => {
  if (s === null || s === undefined) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return `${h}h ${m}min`;
};

export default api;
